import { createSelector } from 'reselect';

import { envConfigSelectors } from '~/selectors';

export const selectBookingsState = (state) => state.bookings;

export const selectBookingCompleted = createSelector(
  selectBookingsState,
  ({ isBookingCompleted }) => isBookingCompleted,
);

export const selectBooking = createSelector(
  selectBookingsState,
  (bookingsState) => (bookingsState && bookingsState.booking) || undefined,
);

export const selectBookingItems = createSelector(
  selectBooking,
  (booking) => (booking && booking.items) || undefined,
);

export const selectMainBookingItem = createSelector(
  selectBookingItems,
  (items) => (items && items[0]) || undefined,
);

export const selectEmail = createSelector(selectMainBookingItem, ({ email } = {}) => email);

export const selectTotalPrice = createSelector(
  selectBooking,
  (booking) => (booking && booking.totalPrice) || undefined,
);

export const selectAnswers = createSelector(selectMainBookingItem, ({ answers = [] } = {}) =>
  answers.map((answer) => ({
    questionId: answer.questionId,
    answer: answer.answers[0],
  })),
);

export const selectSlot = createSelector(selectMainBookingItem, ({ slot } = {}) => slot);

export const selectDateSlot = createSelector(selectSlot, ({ date } = {}) => date);

export const selectTimeSlot = createSelector(selectSlot, ({ time } = {}) => time);

export const selectTimeSlotFrom = createSelector(selectTimeSlot, ({ from } = {}) => from);

export const selectTimeSlotTo = createSelector(selectTimeSlot, ({ to } = {}) => to);

export const selectStore = createSelector(selectMainBookingItem, ({ store } = {}) => store);

export const selectStoreName = createSelector(selectStore, ({ name } = {}) => name);

export const selectStoreAddress = createSelector(selectStore, ({ address } = {}) => address);

export const selectStoreLocation = createSelector(
  selectStoreAddress,
  ({ location } = {}) => location,
);

export const selectStoreLatitude = createSelector(
  selectStoreLocation,
  ({ latitude } = {}) => latitude,
);

export const selectStoreLongitude = createSelector(
  selectStoreLocation,
  ({ longitude } = {}) => longitude,
);

export const selectStoreAddressLines = createSelector(
  selectStoreAddress,
  ({ addressLines } = {}) => addressLines,
);

export const selectPartner = createSelector(selectMainBookingItem, ({ partner } = {}) => partner);

export const selectPartnerName = createSelector(selectPartner, ({ name } = {}) => name);

export const selectDepartment = createSelector(
  selectMainBookingItem,
  ({ department } = {}) => department,
);

export const selectDepartmentName = createSelector(selectDepartment, ({ name } = {}) => name);

export const selectAppointmentType = createSelector(
  selectMainBookingItem,
  ({ appointmentType } = {}) => appointmentType,
);

export const selectAppointmentTypeId = createSelector(selectAppointmentType, ({ id } = {}) => id);

export const selectAppointmentName = createSelector(selectAppointmentType, ({ name } = {}) => name);

export const selectAppointmentCategoryImage = createSelector(
  selectAppointmentType,
  ({ imageUrl } = {}) => imageUrl,
);

export const selectBookingText = createSelector(
  selectMainBookingItem,
  ({ bookingText } = {}) => bookingText,
);

export const selectServiceEnvConfig = createSelector(
  envConfigSelectors.selectServicesEnvConfig,
  selectAppointmentTypeId,
  (servicesEnvConfig, appointmentTypeId) =>
    Object.values(servicesEnvConfig).find((service) =>
      Object.keys(service.appointmentTypes).find((id) => id === appointmentTypeId),
    ) || {},
);

export const selectBookingEnvConfig = createSelector(
  selectServiceEnvConfig,
  selectAppointmentTypeId,
  ({ appointmentTypes } = {}, appointmentTypeId) =>
    appointmentTypes && appointmentTypes[appointmentTypeId],
);

export const selectBookingRemoteV1Status = createSelector(
  selectBookingEnvConfig,
  ({ remote } = {}) => remote === 'v1',
);

export const selectBookingRemoteV2Status = createSelector(
  selectBookingEnvConfig,
  ({ remote } = {}) => remote === 'v2',
);

export const selectBookingRemoteV3Status = createSelector(
  selectBookingEnvConfig,
  ({ remote } = {}) => remote === 'v3',
);

export const selectBookingGender = createSelector(
  selectServiceEnvConfig,
  ({ gender } = {}) => gender,
);

export const selectBookingRemoteStatus = createSelector(
  selectBookingRemoteV1Status,
  selectBookingRemoteV2Status,
  selectBookingRemoteV3Status,
  (isRemoveV1, isRemoteV2, isRemoteV3) => isRemoveV1 || isRemoteV2 || isRemoteV3,
);

export const selectAttendeeQuestionIdsGroup = createSelector(
  selectServiceEnvConfig,
  ({ attendeeQuestionIdsGroup } = {}) => attendeeQuestionIdsGroup,
);

export const selectBookingIsForSomeoneElseQuestionIds = createSelector(
  selectAttendeeQuestionIdsGroup,
  ({ appointmentForSomeoneElseId = '' } = {}) =>
    appointmentForSomeoneElseId
      .split(':')
      .filter(Boolean)
      .map((q) => `q${q}`),
);

export const selectBookingIsForSomeoneElseAnswer = createSelector(
  selectAnswers,
  selectBookingIsForSomeoneElseQuestionIds,
  (bookingAnswers, bookingIsForSomeoneElseQuestionIds) => {
    const answer =
      bookingAnswers.find((a) => bookingIsForSomeoneElseQuestionIds.includes(`q${a.questionId}`)) ||
      {};

    return answer.answer === '1';
  },
);

export const selectBookingIsGiftQuestionIds = createSelector(
  selectAttendeeQuestionIdsGroup,
  ({ giftAppointmentId = '' } = { giftAppointmentId: '' }) =>
    giftAppointmentId
      .split(':')
      .filter(Boolean)
      .map((q) => `q${q}`),
);

export const selectIsGiftAnswer = createSelector(
  selectAnswers,
  selectBookingIsGiftQuestionIds,
  (bookingAnswers, bookingIsGiftQuestionIds) => {
    const answer =
      bookingAnswers.find((a) => bookingIsGiftQuestionIds.includes(`q${a.questionId}`)) || {};

    return answer.answer === '1';
  },
);

export const selectProducts = createSelector(
  selectMainBookingItem,
  (mainBookingItem) => mainBookingItem.products || [],
);

export const selectAddress = createSelector(
  selectMainBookingItem,
  (mainBookingItem) => mainBookingItem?.address,
);

export const selectFormattedAddress = createSelector(
  selectAddress,
  selectStoreName,
  selectStoreAddressLines,
  (address, storeName, storeAddressLines) =>
    address
      ? Object.values(address)
          .filter((item) => !!item)
          .join(', ')
      : `${storeName}, ${storeAddressLines}`,
);

export const bookingsSelectors = {
  selectBookingCompleted,
  selectBookingItems,
  selectMainBookingItem,
  selectEmail,
  selectTotalPrice,
  selectAppointmentType,
  selectAppointmentTypeId,
  selectAppointmentName,
  selectAppointmentCategoryImage,
  selectBookingText,
  selectSlot,
  selectDateSlot,
  selectTimeSlot,
  selectStore,
  selectStoreName,
  selectStoreAddress,
  selectStoreLocation,
  selectStoreAddressLines,
  selectPartner,
  selectDepartment,
  selectServiceEnvConfig,
  selectBookingIsForSomeoneElseAnswer,
  selectBookingRemoteStatus,
  selectBookingRemoteV1Status,
  selectBookingRemoteV2Status,
  selectBookingGender,
  selectIsGiftAnswer,
  selectProducts,
  selectTimeSlotFrom,
  selectTimeSlotTo,
  selectStoreLatitude,
  selectStoreLongitude,
  selectAnswers,
  selectFormattedAddress,
  selectDepartmentName,
  selectPartnerName,
};
